// src/reducers/mainPageReducer.ts
import {GranularityEnum} from "../models/enums/granularity.enum.ts";
import {VisualTagsEnum} from "../models/enums/visualTags.enum.ts";
import {ILanguageConfiguration} from "../models/interfaces/languageConfiguration.interface.ts";
import {NewProjectConfiguration} from "../models/interfaces/newProjectConfigurations.ts";
import iso6391 from "iso-639-1";


export interface ISubtitleState {
  sourceLanguage: Record<string, ILanguageConfiguration>;
  isTranscribeOnly?: boolean;
  targetLanguages: Record<string, ILanguageConfiguration>;
  features?: Record<string, boolean>;
}

export interface IExtendedMetadataState {
  isSelected?: boolean;
  granularity: GranularityEnum;
  shotChangeDetectionSensitivity?: number;
  visualTags: VisualTagsEnum[];
}

export interface IMetadataState {
  isDialogueListSelected?: boolean;
  isExtendedMetadata?: IExtendedMetadataState
}

export interface IAdvancedState {
  isSelected?: boolean;
  audioDescription?: string[]
}

export interface ICreateNewProjectState {
  attachedFiles: File[];
  customConfigurationId?: string;
  customConfigurationName: string;
  fileNameToSeriesNameRelation: Record<string, string>;
  projectTotalDuration: number;
  subtitles: ISubtitleState;
  metadata: IMetadataState;
  advanced: IAdvancedState;
  errors: string[];
}

export type CreateNewProjectAction =
  | { type: "SET_FILES"; payload: File[] }
  | { type: "SET_FILE_SERIES_RELATION"; payload: Record<string, string> }
  | { type: "SET_CUSTOM_CONFIGURATION_ID"; payload: string | undefined }
  | { type: "SET_CUSTOM_CONFIGURATION_NAME"; payload: string }
  | { type: "SET_PROJECT_TOTAL_DURATION"; payload: number }
  | { type: "SET_SOURCE_LANGUAGE"; payload: Record<string, ILanguageConfiguration> }
  | { type: "SET_TARGET_LANGUAGES"; payload: Record<string, ILanguageConfiguration> }
  | { type: "TOGGLE_TRANSCRIBE_ONLY"; }
  | { type: "TOGGLE_FEATURES"; payload: string }
  | { type: "SET_IS_DIALOGUE_LIST_SELECTED"; payload: boolean }
  | { type: "SET_IS_EXTENDED_METADATA_SELECTED"; payload: boolean }
  | { type: "SET_GRANULARITY"; payload: GranularityEnum }
  | { type: "SET_SHOT_CHANGE_DETECTION_SENSITIVITY"; payload: number }
  | { type: "SET_VISUAL_TAGS"; payload: VisualTagsEnum[] }
  | { type: "TOGGLE_AUDIO_DESCRIPTION"; }
  | { type: "SET_AUDIO_DESCRIPTION"; payload: string[] }
  | { type: "SET_ERRORS"; payload: string[] }
  | { type: "FILL_ALL_STEPS_WITH_CONFIGURATION"; payload: NewProjectConfiguration }


export const initialCreateNewProjectState: ICreateNewProjectState = {
  attachedFiles: [] as File[],
  customConfigurationId: undefined,
  customConfigurationName: "",
  fileNameToSeriesNameRelation: {} as Record<string, string>,
  projectTotalDuration: 0,
  subtitles: {
    sourceLanguage: {} as Record<string, ILanguageConfiguration>,
    targetLanguages: {} as Record<string, ILanguageConfiguration>,
    isTranscribeOnly: false,
    features: {} as Record<string, boolean>,
  } as ISubtitleState,
  metadata: {} as IMetadataState,
  advanced: {} as IAdvancedState,
  errors: [],
};

export function newProjectReducer(
  state: ICreateNewProjectState,
  action: CreateNewProjectAction
): ICreateNewProjectState {
  switch (action.type) {
    case "SET_FILES":
      return {...state, attachedFiles: action.payload};
    case "SET_FILE_SERIES_RELATION":
      return {...state, fileNameToSeriesNameRelation: action.payload};
    case "SET_CUSTOM_CONFIGURATION_ID":
      return {...state, customConfigurationId: action.payload};
    case "SET_CUSTOM_CONFIGURATION_NAME":
      return {...state, customConfigurationName: action.payload};
    case "SET_PROJECT_TOTAL_DURATION":
      return {...state, projectTotalDuration: action.payload < 0 ? 0 : action.payload};
    case "SET_SOURCE_LANGUAGE":
      return {
        ...state,
        subtitles: {...state.subtitles, sourceLanguage: action.payload}
      };
    case "SET_TARGET_LANGUAGES":
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          targetLanguages: action.payload,
        }
      };
    case "TOGGLE_TRANSCRIBE_ONLY":
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          targetLanguages: state.subtitles.isTranscribeOnly ? {} : state.subtitles.sourceLanguage,
          isTranscribeOnly: !state.subtitles.isTranscribeOnly
        }
      };
    case "TOGGLE_FEATURES":
      return {
        ...state,
        subtitles: {
          ...state.subtitles, features: {
            ...state.subtitles.features || {},
            [action.payload]: !state.subtitles.features?.[action.payload]
          }
        }
      };
    case "SET_IS_DIALOGUE_LIST_SELECTED":
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isDialogueListSelected: action.payload,
          isExtendedMetadata: {
            isSelected: action.payload ? state.metadata.isExtendedMetadata?.isSelected : false,
            granularity: action.payload ? state.metadata.isExtendedMetadata?.granularity || GranularityEnum.GLIX : GranularityEnum.GLIX,
            visualTags: action.payload ? state.metadata.isExtendedMetadata?.visualTags || [] : [],
          }
        }
      };
    case "SET_IS_EXTENDED_METADATA_SELECTED":
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isExtendedMetadata: {
            isSelected: action.payload,
            granularity: state.metadata.isExtendedMetadata?.granularity || GranularityEnum.GLIX,
            shotChangeDetectionSensitivity: state.metadata.isExtendedMetadata?.shotChangeDetectionSensitivity,
            visualTags: state.metadata.isExtendedMetadata?.visualTags || []
          }
        }
      }
    case "SET_GRANULARITY":
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isExtendedMetadata: {
            isSelected: state.metadata.isExtendedMetadata?.isSelected || false,
            granularity: action.payload,
            shotChangeDetectionSensitivity: state.metadata.isExtendedMetadata?.shotChangeDetectionSensitivity,
            visualTags: state.metadata.isExtendedMetadata?.visualTags || []
          }
        }
      };
    case "SET_SHOT_CHANGE_DETECTION_SENSITIVITY":
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isExtendedMetadata: {
            isSelected: state.metadata.isExtendedMetadata?.isSelected || false,
            granularity: state.metadata.isExtendedMetadata?.granularity || GranularityEnum.GLIX,
            shotChangeDetectionSensitivity: action.payload,
            visualTags: state.metadata.isExtendedMetadata?.visualTags || []
          }
        }
      }
    case "SET_VISUAL_TAGS":
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isExtendedMetadata: {
            isSelected: state.metadata.isExtendedMetadata?.isSelected || false,
            granularity: state.metadata.isExtendedMetadata?.granularity || GranularityEnum.GLIX,
            shotChangeDetectionSensitivity: state.metadata.isExtendedMetadata?.shotChangeDetectionSensitivity,
            visualTags: action.payload
          }
        }
      };
    case "TOGGLE_AUDIO_DESCRIPTION":
      return {
        ...state,
        advanced: {
          ...state.advanced,
          isSelected: !state.advanced.isSelected,
        }
      }
    case "SET_AUDIO_DESCRIPTION":
      return {
        ...state,
        advanced: {...state.advanced, audioDescription: action.payload}
      };
    case "SET_ERRORS":
      return {
        ...state,
        errors: action.payload
      };
    case "FILL_ALL_STEPS_WITH_CONFIGURATION": {
      const configuration: NewProjectConfiguration = action.payload;
      return {
        ...state,
        subtitles: {
          sourceLanguage: {[iso6391.getName(configuration.sourceLanguage)]: configuration.config.sourceTranscriptionConfig || {}},
          isTranscribeOnly: Object.keys(configuration.config.subtitles).length === 0,
          targetLanguages: configuration.config.subtitles,
          features: {
            shotChangeSync: !!configuration.config.shotChangeSync,
          }
        },
        metadata: {
          isDialogueListSelected: !!configuration.config?.dialogueListConfig,
          isExtendedMetadata: {
            isSelected: !!configuration.config?.dialogueListConfig?.extendedMetadata,
            granularity: configuration.config?.dialogueListConfig?.extendedMetadata?.granularity || GranularityEnum.GLIX,
            shotChangeDetectionSensitivity: configuration.config?.dialogueListConfig?.extendedMetadata?.shotChangeDetectionSensitivity,
            visualTags: configuration.config?.dialogueListConfig?.extendedMetadata?.visualClassifiers || []
          }
        },
        advanced: {
          isSelected: !!configuration.config.audioDescriptions,
          audioDescription: configuration.config.audioDescriptions?.targetLanguages || []
        },
      };

    }
    default:
      return state;

  }
}